import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Session-scoped dismissal — cleared on next login (see AuthContext.login).
const DISMISS_KEY = "wr:intakeBannerDismissed";

// Shown only to founders with an INCOMPLETE first-day intake. Progress is server-derived
// from GET /api/intake/status (preserved across devices/sessions). No extra model call.
export function IntakeResumeBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");

  const isOperator = user?.role === "operator";

  useEffect(() => {
    if (!user || isOperator) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/intake/status");
        if (active) setStatus(data);
      } catch (e) {
        /* silent — the banner simply won't render */
      }
    })();
    return () => {
      active = false;
    };
  }, [user, isOperator]);

  if (!user || isOperator || dismissed) return null;
  if (!status || status.onboarding_complete) return null;

  const next = status.missing_areas?.[0];

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="intake-resume-banner"
      className="border-b border-wr-border border-l-2 border-l-wr-red bg-wr-redMuted"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center gap-4 flex-wrap">
        <Sparkles size={16} className="text-wr-red shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm text-white">
            Finish your first-day session —{" "}
            <span data-testid="intake-resume-progress" className="wr-mono text-wr-red">
              {status.completeness}% complete
            </span>
            .
          </p>
          {next && (
            <p className="wr-label !text-[0.62rem] mt-0.5" data-testid="intake-resume-next">
              Next: {next}
            </p>
          )}
        </div>
        <div className="h-1.5 w-28 bg-wr-surface2 overflow-hidden hidden sm:block">
          <div className="h-full bg-wr-red transition-all duration-500" style={{ width: `${status.completeness}%` }} />
        </div>
        <button
          type="button"
          data-testid="intake-resume-continue"
          onClick={() => navigate("/intake")}
          className="wr-mono uppercase tracking-wider text-xs bg-wr-red hover:bg-wr-redHover text-white px-3 py-2 flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white"
        >
          Continue <ArrowRight size={13} />
        </button>
        <button
          type="button"
          data-testid="intake-resume-dismiss"
          onClick={dismiss}
          className="wr-label hover:text-white flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-wr-red px-1"
        >
          Not now <X size={12} />
        </button>
      </div>
    </div>
  );
}
